import React, { useEffect } from "react";
// ** MUI Components
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardHeader,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";

// ** Custom Component Import
import CustomTextField from "../../components/mui/text-field";

// ** Custom Components Imports
import OptionsMenu from "../../components/option-menu";
// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";

// ** Icon Imports
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import Icon from "../../components/icon";

// ** Third Party Imports
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NProgress from "react-nprogress";

// ** Component Imports
import TagManagementDialog from './component/TagManagement';
import AdvancedFilter from "./component/AdvancedFilter";
import BulkImport from "../../components/BulkImport";
import AddCustomerDialog from "./component/AddCustomer";
import QuickView from "./component/QuickView";
import AddJobsiteDialog from "./component/AddJobsite";
import { useIndexLogic } from "./logic/indexLogic";
import { useCustomerPermission } from "./permissions/CustomerPermissions";
import { useGridApiRef } from '@mui/x-data-grid-pro';
const Customers = () => {
  // ** Props
  const { PermissionProps } = useCustomerPermission();
  const {
    hasSavePermission,
    hasSaveIndividualPermission,
    hasDeletePermission,
  } = PermissionProps;
  const {
    IndexProps
  } = useIndexLogic();

  const { 
    tagManagementData,
    openNewCustomerDlg,
    data,
    loading,    
    contentHeightFixed,
    buttons,
    selectedButton,
    paginationText,
    searchText,
    bulkCusType,  
    bulkCusStatus, 
    bulkCusLead,  
    selectedRows,
    columns,
    paginationModel,
    rowSelectionModel,
    openBulkImportDlg,
    EmpFiles,
    btnLoader,
    openTagManagementDlg,
    bulkTags,    
    filteredRows,
    BulkImportContent,
    getRootProps,
    getInputProps,
    setBulkTags,
    setEmpFiles,  
    setRowSelectionModel,
    handleSearchChange,
    navigate,
    setOpenNewCustomerDlg,
    handleOpenNewCustomerDlg,
    setLoading,
    NoRowsOverlay,
    handleExcelExport,
    handleCSVExport,
    handleCloseBulkImportDlg,
    handleOpenBulkImportCustomerDlg,
    handleOpenBulkImportCustomerWithJobsitesDlg,
    handleOpenBulkImportCustomerWithContactsDlg,
    handleClearSelection,
    handleBulkOperations,
    handleBulkCusLeadChange,
    handleBulkCusStatusChange,
    handleBulkCusTypeChange,
    handleGridSelection,
    showBulkConfirmation,
    handleClearSearch,
    handlePaginationModel,
    handleCloseTagManagementDlg,
    handleFilterTypeChange,
    handleOpenTagManagementDlg,
    handleReset,
    applyFilters,   
    filteredData,
    fetchData,
    allCustomers, 
    handleAddTag,
    handleDeleteTag,
    Tag,
    setTag,
    customer,
    showJobsites,
    flattenedJobsitesData,
    jobsitesColumns,
    quickViewOpen,
    selectedJobsite,
    alertList,
    propertyAreaList,
    handleQuickViewClose,
    openNewJobsiteDlg,
    setOpenNewJobsiteDlg,
    jobsiteForEdit,
    jobsiteContent,
    handleCloseNewJobsiteDlg,
    handleJobsiteChange,
    activeStepJobsite,
    setActiveStepJobsite,
    propertyArea,
    setPropertyArea,
    areaMeasurement,
    setAreaMeasurement,
    clearAreaMeasurement,
    fetchPropertyArea,
    clearPropertyArea,
    files,
    setFiles,
    listOfMeasurements,
    setListOfMeasurements,
    fetchAlerts,
    fetchContacts,
    allContacts,
    setAllContacts,
    setAlertList,
    filteredJobsitesRows,
  } = IndexProps;

  // ** Hooks
  const apiRef = useGridApiRef();
  const theme = useTheme();
  const smAbove = useMediaQuery(theme.breakpoints.up("sm"));
  const smBelow = useMediaQuery(theme.breakpoints.down("sm"));
   useEffect(() => {
    if (!apiRef.current) return;

    apiRef.current.autosizeColumns({
      includeHeaders: true,
      includeOutliers: true,
      expand: false,
    });
  }, [showJobsites, filteredRows, filteredJobsitesRows]);
  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Customers"}
    >
      <Card sx={{ position: 'relative' }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-start" },
                  alignItems: 'center',
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: { xs: "center", sm: "flex-start" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {buttons.map((button, index) => (
                    <Button
                      key={index}
                      color={selectedButton === index ? "main" : "secondary"}
                      variant={selectedButton === index ? "contained" : "tonal"}
                      onClick={() =>
                        handleFilterTypeChange(index, button.searchKeyWord)
                      }
                      sx={{
                        minWidth: { xs: "30%", sm: "auto" },
                        flex: { xs: "1 1 auto", sm: "0 1 auto" },
                      }}
                    >
                      {button.name}
                    </Button>
                  ))}
                </Box>
              </Box>
              {smAbove && (
                <Divider orientation="vertical" flexItem sx={{ mx: 4 }} />
              )}
              <Box
                sx={{
                  border: "1px solid #D9D9D9",
                  borderRadius: 1,
                  px: { xs: 2, sm: 4 },
                  py: 2,
                  width: { xs: "100%", sm: "auto" },
                  textAlign: { xs: "center", sm: "left" },
                  mt: { xs: 2, sm: 0 },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  {showJobsites 
                    ? `Displaying ${paginationText} jobsites`
                    : `Displaying ${paginationText} records`}
                </Typography>
              </Box>
            </Box>
          }
          titleTypographyProps={{ sx: { mb: [1, 0] }, width: '100%' }}
          action={
            <Box 
              sx={{ 
                display: "flex", 
                gap: { xs: 2, sm: 3 }, 
                flexWrap: "wrap",
                width: { xs: '100%', sm: 'auto' },
                mt: { xs: 1, sm: 0 },
                justifyContent: { xs: 'space-between', sm: 'flex-end' }
              }}
            >
              <CustomTextField
                value={searchText}
                placeholder="Search"
                onChange={(e) => handleSearchChange(e.target.value)}
                size="small"
                sx={{
                  width: { xs: '100%', sm: '200px', md: '250px' },
                  '& .MuiInputBase-root': {
                    width: { xs: '100%', sm: 'auto' },
                  },
                  order: { xs: 1, sm: 1 }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="tabler:search" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText && (
                        <IconButton
                          edge="end"
                          onClick={handleClearSearch}
                          aria-label="clear"
                          sx={{ mr: 2 }}
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {!showJobsites && (
                <AdvancedFilter
                  onApplyFilters={applyFilters}
                  handleReset={handleReset}
                  filtered={filteredData}
                  sx={{
                    order: { xs: 3, sm: 2 },
                  }}
                />
              )}
              <Button
                variant="contained"
                color="main"
                startIcon={<AddIcon />}
                disabled={!hasSavePermission}
                {...(hasSavePermission && {
                  onClick: handleOpenNewCustomerDlg
                })}
                sx={{
                  order: { xs: 2, sm: 3 },
                  flex: { xs: '1 1 auto', sm: '0 1 auto' }
                }}
              >
                  New Customer
              </Button>
              <OptionsMenu
                iconButtonProps={{
                  sx: { color: "text.secondary", order: { xs: 4, sm: 4 } },
                }}
                options={[
                  {
                    text: "Import Customers",
                    icon: <UploadIcon fontSize="small" sx={{ mr: 1 }}/>,
                    menuItemProps: {
                      onClick: handleOpenBulkImportCustomerDlg,
                    },
                  },
                  {
                    text: "Import Customers with Jobsites",
                    icon: <UploadIcon fontSize="small" sx={{ mr: 1 }}/>,
                    menuItemProps: {                      
                      onClick: handleOpenBulkImportCustomerWithJobsitesDlg,
                    },
                  },
                  {
                    text: "Import Customers with Contacts",
                    icon: <UploadIcon fontSize="small" sx={{ mr: 1 }}/>,
                    menuItemProps: {
                      onClick: handleOpenBulkImportCustomerWithContactsDlg,
                    },
                  },
                 {
                        text: "Export",
                        icon: <DownloadIcon fontSize="small" sx={{ mr: 1 }}/>,
                        menuItemProps: {
                          onClick: () => {
                            Swal.fire({
                            title: '<strong>Export</strong>',
                        
                              html: `
                              <style>
                              .swal2-popup{
                                background-color: ${theme.palette.mode === 'dark' ? '#1B2431' : '#fff'};
                              }
                              .exportStyle{
                                  display: flex; 
                                  align-items: center; 
                                  justify-content: center;
                                
                                  gap: 20px;
                                  height: 150px;
                                  margin-top: 15px; /* Added for consistent spacing */
                              }       
                              .custom-close-btn {
                                  position: absolute;
                                  right: 10px;
                                  top: 12px;
                                  width: 30px;
                                  height: 30px;
                                  cursor: pointer;
                                  z-index: 1000;
                                  color: #555;
                                  font-size: 30px;
                                  line-height: 38px;
                                  text-align: center;
                                  background: none;
                                  border: none;
                                  padding: 0;
                                  margin: 0;
                              }
                              .custom-close-btn:hover {
                                  color: #333;
                              }                     
                              .iconBorder{
                                    border: 1px solid transparent; /* Made border transparent */
                                  border-radius: 8px;
                                  padding: 15px;
                                  cursor: pointer;
                                  transition: all 0.3s ease;
                                  margin-top: 15px; /* Adjusted for consistent spacing */
                                  position: relative;
                                  width: 130px;
                                  height: 145px; /* Set to 140px as requested */
                                  display: flex;
                                  flex-direction: column;
                                  align-items: center;
                                  justify-content: center;
                                    box-shadow: 
                                  0 0 0 1px rgba(0, 0, 0, 0.12), /* Black border effect at 12% opacity */
                                  0 0 20px #10182808; /* Original shadow */
                              }
                              .selected{
                                  border: 2px solid #23a26d !important;
                                  background-color: #f5fff9 !important;
                              }
                              .selected::after {
                                  content: "";
                                  position: absolute;
                                  top: 10px;
                                  right: 10px;
                                  width: 16px;
                                  height: 16px;
                                  border-radius: 50%;
                                  background-color: #23a26d;
                                  border: 3px solid white;
                                  box-shadow: 0 0 0 1px #23a26d;
                              }
                              .descriptionText{
                                  text-align:left;
                                  margin-bottom: 25px;
                                  margin-top: 20px;
                                  color: #555;
                              }
                              .swal2-html-container {
                                  padding: 0em 1.6em 1.3em !important;
                              }
                              .exportIcons{
                                  margin-bottom: 10px;
                              }
                              .format-label {
                                  font-size: 14px;
                                  color: #333;
                                  margin-top: 8px;
                              }
                              .swal2-close {
                                  color: #000 !important;
                                  font-size: 20px !important;
                                  width: 24px !important;
                                  height: 34px !important;
                              }
                              .swal2-close:hover {
                                  color: #000 !important;
                              }
                              .swal2-cancel{
                                  background: transparent;
                                  color: #313D4F;
                                  border: 1px solid #313D4F;
                              }
                              .swal2-cancel:hover{
                                  background-color: rgba(49, 61, 79, 0.08) !important;
                                  border: 1px solid #313D4F;
                                  background-image: none;
                              }
                              .swal2-confirm{
                                  background-color: #23a26d;
                              }
                              .swal2-confirm:hover{
                                  background-color: #1d8a5a !important;
                              }
                            
                          </style>
                        <div>
                          <button class="custom-close-btn" id="customCloseBtn">×</button>
                        
                                <p>Choose your preferred file format to export your data</p>
                          <div class="exportStyle">                            
                            <div class="iconBorder" id="CSV-Container">
                              <img src="./images/csv.png" class="exportIcons" id="exportCsv" width="50" height="50">
                              <span class="format-label">CSV File</span>                             
                            </div>  
                            <div class="iconBorder" id="XLSX-Container">
                              <img src="./images/xlsx.png" class="exportIcons" id="exportXlsx" width="50" height="50">
                              <span class="format-label">XLSX File</span>
                            </div>                                                            
                          </div>
                        <div>                          
                        `,
                        showCancelButton: true,
                        confirmButtonText: 'Export',
                    cancelButtonText: 'Cancel',
                      
                        confirmButtonColor: "#23a26d",
                        customClass: {
                      popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
                    },
                        showCloseButton: false,
                        focusConfirm: false,
                        width: "620px",
                        didOpen: () => {
                            document.getElementById('customCloseBtn').addEventListener('click', () => {
                            Swal.close();
                          });
                          
                          const exportButton = document.querySelector(".swal2-confirm");
                          exportButton.disabled = true;
                          
                          document.querySelectorAll(".iconBorder").forEach(option => {
                            option.addEventListener("click", function() {
                              document.querySelectorAll(".iconBorder").forEach(opt => 
                                opt.classList.remove("selected"));
                              this.classList.add("selected");
                              exportButton.disabled = false;
                            });
                          });
                        },
                      }).then((result) => {
                        if (result.isConfirmed) {
                          const selected = document.querySelector(".iconBorder.selected");
                          if (selected?.id === "XLSX-Container") {
                            handleExcelExport();
                          } else {
                            handleCSVExport();
                          }
                        }
                      });
                    },
                  },
                }
                ]}
              />
            </Box>
          }
          sx={{
            py: { xs: 2, sm: 4 },
            flexDirection: { xs: "column", sm: "row" },
            "& .MuiCardHeader-action": { m: 0 },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 0 }
          }}
        />
        {selectedRows.length > 0 && !showJobsites && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 4 },
              alignItems: { xs: "flex-start", sm: "center" },
              mx: { xs: 2, sm: 6 },
              mt: 2,
              mb: 4,
              px: { xs: 4, sm: 0 }
            }}
          >
            {smBelow && (
              <Divider />
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #E7E7E7",
                borderRadius: 1,
                pr: { xs: 2, sm: 4 },
                width: { xs: '100%', sm: 'auto' },
                order: { xs: 1, sm: 1 }
              }}
            >
              <IconButton sx={{ mx: 1 }} onClick={handleClearSelection}>
                <Icon icon="tabler:x" />
              </IconButton>
              <Typography
                variant="body1"
                sx={{
                  cursor: "default",
                  userSelect: "none",
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                {selectedRows.length} {selectedRows.length === 1 ? "Record" : "Records"} Selected
              </Typography>
            </Box>
            <CustomTextField
              select
              disabled={!hasSaveIndividualPermission}
              defaultValue={bulkCusLead || "0"}
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: '120px' },
                '& .MuiInputBase-root': {
                  width: { xs: '100%', sm: 'auto' },
                },
                order: { xs: 2, sm: 2 }
              }}
              {...(hasSaveIndividualPermission && {
                onChange: (e) => handleBulkCusLeadChange(e.target.value),
              })}
            >
              <MenuItem value="0" disabled>Lead</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </CustomTextField>
            <CustomTextField
              select
              disabled={!hasSaveIndividualPermission}
              defaultValue={bulkCusStatus || "0"}
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: '120px' },
                '& .MuiInputBase-root': {
                  width: { xs: '100%', sm: 'auto' },
                },
                order: { xs: 3, sm: 3 }
              }}
              {...(hasSaveIndividualPermission && {
                onChange: (e) => handleBulkCusStatusChange(e.target.value),
              })}
            >
              <MenuItem value="0" disabled>Status</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </CustomTextField>
            <Button
              variant="contained"
              color="main"
              disabled={!hasSaveIndividualPermission}
              sx={{
                order: { xs: 4, sm: 4 },
                width: { xs: '100%', sm: 'auto' }
              }}
              {...(hasSaveIndividualPermission && {
                onClick: () => handleBulkOperations(),
              })}
            >
              Apply Changes
            </Button>
            <Divider 
              orientation="vertical" 
              flexItem 
              sx={{ 
                display: { xs: 'none', sm: 'block' },
                order: { xs: 5, sm: 5 }
              }} 
            />
            <Box
              sx={{
                border: "1px solid #D9D9D9",
                borderRadius: 1,
                display: "flex",
                gap: { xs: 1, sm: 2 },
                px: { xs: 1, sm: 2 },
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'space-around', sm: 'flex-start' },
                order: { xs: 6, sm: 6 }
              }}
            >
              <Tooltip title="Tag Management">
                <IconButton 
                  onClick={handleOpenTagManagementDlg}
                >
                  <Icon icon="tabler:tag" />
                </IconButton>
              </Tooltip>
              <Divider 
                orientation="vertical" 
                flexItem 
                sx={{ 
                  display: { xs: 'block', sm: 'none' },
                }} 
              />
              <Tooltip title="Delete Selected">
                <IconButton
                  color="error"
                  disabled={!hasDeletePermission}
                  {...(hasDeletePermission && {
                    onClick: () =>
                      showBulkConfirmation(
                        Object.values(selectedRows).length,
                        `Do you want to delete ${
                          Object.values(selectedRows).length
                        } ${
                          Object.values(selectedRows).length === 1
                            ? "customer"
                            : "customers"
                        }? You won't be able to revert this!`,
                        "Delete",
                        [],
                        `${
                          Object.values(selectedRows).length === 1
                            ? "Yes, delete it"
                            : "Yes, delete them"
                        }`
                      ),
                  })}
                >
                  <Icon icon="tabler:trash" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        <Box sx={{ height: "75vh" }}>
          <DataGridPro
           apiRef={apiRef}
            editMode="row"
            // pagination
            rows={showJobsites ? filteredJobsitesRows : filteredRows}
            rowHeight={62}
            columns={showJobsites ? jobsitesColumns : columns}
            checkboxSelection={!showJobsites}
            // pageSizeOptions={[10, 50, { value: -1, label: "Show All" }]}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            // paginationModel={paginationModel}
            // onPaginationModelChange={handlePaginationModel}
            selectionModel={showJobsites ? [] : rowSelectionModel}
            onSelectionModelChange={(newSelectionModel) => {
              if (!showJobsites) {
                handleGridSelection(newSelectionModel);
                setRowSelectionModel(newSelectionModel);
                if (newSelectionModel.length === 0) {
                  handleBulkCusLeadChange(0);
                  handleBulkCusStatusChange(0);
                }
              }
            }}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "none",
              },
              // Add left padding to first column header when showing jobsites
              ...(showJobsites && {
                "& .MuiDataGrid-columnHeader:first-of-type .MuiDataGrid-columnHeaderTitleContainer": {
                  paddingLeft: 3,
                },
              }),
            }}
            slots={{
              noRowsOverlay: NoRowsOverlay,
              footer: () => null
            }}
            disableExtendRowFullWidth
            initialState={{
              sorting: {
                sortModel: showJobsites 
                  ? [{ field: "jobsiteName", sort: "asc" }]
                  : [{ field: "Fullname", sort: "asc" }],
              },
              columns: {
                columnVisibilityModel: showJobsites ? {} : {
                  id: false,
                  lastService: false,
                  lastActivity: false,
                },
              },
            }}
          />
        </Box>

        {/* QuickView for Jobsites - positioned relative to Card */}
        {quickViewOpen && selectedJobsite && (
          <QuickView
            handleQuickViewClose={handleQuickViewClose}
            jobsite={selectedJobsite}
            contacts={allContacts}
            propertyAreaList={propertyAreaList}
            alertList={alertList}
            setAlertList={setAlertList}
          />
        )}
      </Card>

      {/* Add Customers */}
      <AddCustomerDialog
        open={openNewCustomerDlg}
        setOpenNewCustomerDlg={setOpenNewCustomerDlg}
        fetchData={fetchData}
        allCustomers={allCustomers}
      />

      {/* Import Customers */}
      <BulkImport
        open={openBulkImportDlg}
        handleClose={handleCloseBulkImportDlg}
        content={BulkImportContent}
        files={EmpFiles}
        loading={btnLoader}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        setFiles={setEmpFiles}
        setLoading={setLoading}
        navigate={navigate}
      />      

      {/* Bulk Operations Tag Management Dialog */}
      {/* <TagManagementDialog 
    open={openTagManagementDlg}
    bulkTags={bulkTags} 
    setBulkTags={setBulkTags}
    handleClose={handleCloseTagManagementDlg}
    data={tagManagementData}
  /> */}

      <TagManagementDialog
        open={openTagManagementDlg}
        handleClose={handleCloseTagManagementDlg}
        data={{ title: "Tag Management" }}
        handleAddTag={handleAddTag}
        handleDeleteTag={handleDeleteTag}
        tags={customer?.tags}
        tag={Tag}
        setTag={setTag}
        hasSaveIndividualPermission={hasSaveIndividualPermission}
      />

     
      {/* Add Jobsite Dialog */}
      <AddJobsiteDialog 
        open={openNewJobsiteDlg}
        setJobsiteOpen={setOpenNewJobsiteDlg}
        handleClose={handleCloseNewJobsiteDlg}
        customer={jobsiteForEdit ? { 
          customerID: jobsiteForEdit.customerID,
          firstName: '',
          lastName: '',
          companyName: '',
          customerType: 'Individual'
        } : { customerID: 0 }}
        fetchContactData={fetchContacts}
        toast={toast}
        jobsite={jobsiteForEdit || { 
          jobsiteID: 0, 
          customerID: 0, 
          jobsiteName: '', 
          jobsiteType: 'Residential',
          street1: '',
          city: '',
          state: '',
          zipCode: '',
          billingAddress: false,
          isActive: false
        }}
        handleJobsiteChange={handleJobsiteChange}      
        navigate={navigate} 
        NProgress={NProgress}
        fetchContacts={fetchContacts}  
        contacts={allContacts}    
        propertyArea={propertyArea}
        setPropertyArea={setPropertyArea}
        areaMeasurement={areaMeasurement} 
        setAreaMeasurement={setAreaMeasurement}
        setLoading={setLoading}
        fetchJobsites={fetchData}
        activeStep={activeStepJobsite}
        setActiveStep={setActiveStepJobsite}
        clearAreaMeasurement={clearAreaMeasurement}
        fetchPropertyArea={fetchPropertyArea}
        propertyAreaList={propertyAreaList}
        clearPropertyArea={clearPropertyArea}
        jobsiteContent={jobsiteContent} 
        alertList={alertList}
        setAlertList={setAlertList}        
        files={files}
        setFiles={setFiles}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 10000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default Customers;